// General resource substitution: "you may spend X where Y is required".
// Plan: docs/resource-substitution-plan.md
//
// One rule shape covers glory conversion, whim-for-actions and cards like D'Vana Tendi:
//
//     {from: 'latinum', to: 'dilithium', give: 1, get: 1}
//         -> give `give` units of `from` to cover `get` units of `to`
//
// getCostInfo() turns a cost into a `need` map, hands it here with the player's wallet and
// the rules in force, and gets back an `alloc`:
//
//     need  {dilithium: 3}                           what the cost asks for
//     alloc {dilithium: {dilithium: 1, glory: 1}}    1 dilithium + 1 glory (covering 2) pay it
//     spend {dilithium: 1, glory: 1}                 what actually leaves the player
//
// Deliberately import-free: the cost info object is a replayable choice (see the plan, §5), so
// nothing here may close over game state, and the test runs under plain node with no bundler.

// Glory conversion, which no card grants - one glory covers two dilithium or one latinum.
// This is the table that used to live as `gloryConversions` in costs.js.
export const BUILTIN_SUBSTITUTIONS = [
    {from: 'glory', to: 'dilithium', give: 1, get: 2, builtin: true},
    {from: 'glory', to: 'latinum', give: 1, get: 1, builtin: true}
];

// Spending glory costs a victory point, so the solver should reach for anything else first.
// Everything else is weighted equally; `prefer` lets a caller (the bot) re-weight.
const DEFAULT_WEIGHTS = {glory: 10};
const BASE_WEIGHT = 1;

// Rendering and payment order, so logs and cost strings stay stable across changes.
export const SPEND_ORDER = ['glory', 'dilithium', 'latinum', 'whim', 'actions'];

// Beyond this many search nodes, fall back to the greedy allocation rather than hang.
const NODE_CAP = 200000;

function possArray(v){
    if(v === null || v === undefined) return [];
    return Array.isArray(v) ? v : [v];
}

function num(v, dflt = 1){
    let n = typeof v == 'string' ? parseInt(v) : v;
    if(typeof n != 'number' || !isFinite(n) || n < 1) return dflt;
    return Math.floor(n);
}

// 'action' is how the grammar names it; the player field is `actions`.
export function normaliseResource(r){
    if(r == 'action') return 'actions';
    return r;
}

function normaliseRule(r){
    if(!r || !r.from || !r.to) return false;
    let from = normaliseResource(r.from);
    let to = normaliseResource(r.to);
    if(from == to) return false; // a rule that substitutes a resource for itself is a no-op
    return {...r, from, to, give: num(r.give), get: num(r.get)};
}

function ruleKey(r){
    return `${r.from}>${r.to}:${r.give}/${r.get}`;
}

// A blocker matches a rule when both ends agree; an absent `from` blocks every route into `to`.
function blocks(blocker, rule){
    if(!blocker) return false;
    let to = normaliseResource(blocker.to);
    if(to && to != rule.to) return false;
    let from = blocker.from ? normaliseResource(blocker.from) : false;
    if(from && from != rule.from) return false;
    return !!(to || from);
}

// Assemble the rules in force: the builtins plus whatever cards grant, minus whatever cards
// block. `granted`/`blocked` are the raw ongoing payloads, so callers need not tidy them.
//
// bot: the bot charts do not model spending glory (a victory point), so a bot only ever gets
// lossless rules - give == get, with the reverse rule also present, meaning the two piles are
// interchangeable and which one drains is not a strategic choice.
export function substitutions(granted = [], blocked = [], {bot = false} = {}){
    let rules = [];
    let seen = new Set();
    for(let r of [...BUILTIN_SUBSTITUTIONS, ...possArray(granted).flat()]){
        let rule = normaliseRule(r);
        if(!rule) continue;
        let key = ruleKey(rule);
        if(seen.has(key)) continue;
        seen.add(key);
        rules.push(rule);
    }
    let stoppers = possArray(blocked).flat();
    rules = rules.filter(r => !stoppers.some(b => blocks(b, r)));
    rules = rules.map(r => ({
        ...r,
        lossless: r.give == r.get && rules.some(o => o.from == r.to && o.to == r.from && o.give == o.get)
    }));
    if(bot) rules = rules.filter(r => r.lossless);
    // Stable order: the solver's tie-breaks depend on it, and a replay must reach the same
    // allocation as the session that recorded it.
    return rules.sort((a, b) => ruleKey(a) < ruleKey(b) ? -1 : 1);
}

function weightFor(resource, prefer){
    if(prefer && Object.prototype.hasOwnProperty.call(prefer, resource)) return prefer[resource];
    if(Object.prototype.hasOwnProperty.call(DEFAULT_WEIGHTS, resource)) return DEFAULT_WEIGHTS[resource];
    return BASE_WEIGHT;
}

// Substituted coverage dominates the score, so the solver always pays natively where it can -
// which is what the glory-only code did, and what a player expects.
const COVER_WEIGHT = 100000;

// The payers for one requirement: the resource itself, then each rule that produces it.
// Native first so it wins every tie.
function payersFor(to, rules, noSub){
    let payers = [{from: to, give: 1, get: 1, native: true}];
    if(!noSub.has(to)) payers = [...payers, ...rules.filter(r => r.to == to)];
    return payers;
}

function emptyAlloc(needs){
    let alloc = {};
    for(let n of needs) alloc[n.to] = {};
    return alloc;
}

export function spendFrom(alloc){
    let spend = {};
    for(let to of Object.keys(alloc)){
        for(let from of Object.keys(alloc[to])){
            let units = alloc[to][from];
            if(!units) continue;
            spend[from] = (spend[from] || 0) + units;
        }
    }
    return spend;
}

// Enumerate the ways `payers` can cover `remaining` units of one requirement out of `wallet`.
// Counts are capped at ceil(remaining / get): more than that is pure waste and never helps.
function* combinations(payers, remaining, wallet, idx = 0, taken = []){
    if(remaining <= 0){
        yield taken.slice();
        return;
    }
    if(idx >= payers.length) return;
    let p = payers[idx];
    let max = Math.min(
        Math.floor((wallet[p.from] || 0) / p.give),
        Math.ceil(remaining / p.get)
    );
    // High to low, so the greedy-looking answer is found first and the search settles quickly.
    for(let k = max; k >= 0; k--){
        taken.push(k);
        yield* combinations(payers, remaining - k * p.get, subtract(wallet, p.from, k * p.give), idx + 1, taken);
        taken.pop();
    }
}

function subtract(wallet, resource, amount){
    if(!amount) return wallet;
    let next = {...wallet};
    next[resource] = (next[resource] || 0) - amount;
    return next;
}

function walletKey(wallet, keys){
    return keys.map(k => wallet[k] || 0).join(',');
}

// Exact search: for each requirement in turn, try every feasible combination of payers and keep
// the cheapest completion. Memoised on (requirement index, remaining wallet), which collapses
// the orderings that reach the same state.
function search(needs, wallet, rules, noSub, prefer){
    // Only resources that can actually pay for something belong in the memo key.
    let keys = Array.from(new Set(needs.flatMap(n => payersFor(n.to, rules, noSub).map(p => p.from)))).sort();
    let memo = new Map();
    let nodes = 0;

    function solve(idx, w){
        if(idx >= needs.length) return {score: 0, picks: []};
        let key = idx + '|' + walletKey(w, keys);
        if(memo.has(key)) return memo.get(key);
        if(++nodes > NODE_CAP) throw new RangeError('spend_as: search cap');

        let need = needs[idx];
        let payers = payersFor(need.to, rules, noSub);
        let best = null;
        for(let counts of combinations(payers, need.amount, w)){
            let next = w;
            let score = 0;
            for(let i = 0; i < counts.length; i++){
                if(!counts[i]) continue;
                let p = payers[i];
                next = subtract(next, p.from, counts[i] * p.give);
                score += counts[i] * p.give * weightFor(p.from, prefer);
                if(!p.native) score += counts[i] * p.get * COVER_WEIGHT;
            }
            let rest = solve(idx + 1, next);
            if(!rest) continue;
            let total = score + rest.score;
            if(!best || total < best.score){
                best = {score: total, picks: [counts.slice(), ...rest.picks]};
            }
        }
        memo.set(key, best);
        return best;
    }

    let found = solve(0, wallet);
    if(!found) return false;
    let alloc = emptyAlloc(needs);
    for(let i = 0; i < needs.length; i++){
        let payers = payersFor(needs[i].to, rules, noSub);
        let counts = found.picks[i];
        for(let j = 0; j < counts.length; j++){
            if(!counts[j]) continue;
            alloc[needs[i].to][payers[j].from] = (alloc[needs[i].to][payers[j].from] || 0) + counts[j] * payers[j].give;
        }
    }
    return alloc;
}

// Native first, then the cheapest rule by weighted rate. Correct whenever no pay-resource is
// contested between two requirements; used only when the exact search hits its node cap.
function greedy(needs, wallet, rules, noSub, prefer){
    let alloc = emptyAlloc(needs);
    let left = {...wallet};
    for(let need of needs){
        let remaining = need.amount;
        let payers = payersFor(need.to, rules, noSub).slice().sort((a, b) => {
            if(a.native != b.native) return a.native ? -1 : 1;
            return (a.give * weightFor(a.from, prefer)) / a.get - (b.give * weightFor(b.from, prefer)) / b.get;
        });
        for(let p of payers){
            if(remaining <= 0) break;
            let want = Math.ceil(remaining / p.get);
            let can = Math.min(want, Math.floor((left[p.from] || 0) / p.give));
            if(can <= 0) continue;
            alloc[need.to][p.from] = (alloc[need.to][p.from] || 0) + can * p.give;
            left[p.from] -= can * p.give;
            remaining -= can * p.get;
        }
        if(remaining > 0) return false;
    }
    return alloc;
}

/**
 * Work out how to pay `need` out of `wallet` under `rules`.
 *
 * @param need   {resource: amount} the cost, after reductions
 * @param wallet {resource: amount} what the player has to hand (never card-held tokens)
 * @param rules  from substitutions()
 * @param noSub  resources that must be paid natively (a cost item marked noConversion)
 * @param prefer optional {resource: weight} overriding how reluctantly a pile is drained
 * @returns {ok, alloc, spend, shortfall}
 */
export function allocate({need = {}, wallet = {}, rules = [], noSub = [], prefer = null} = {}){
    let blockNative = new Set(possArray(noSub).map(normaliseResource));
    // Fixed order in, fixed answer out: a replay must not depend on key insertion order.
    let needs = Object.keys(need)
        .filter(r => need[r] > 0)
        .sort()
        .map(r => ({to: r, amount: need[r]}));

    if(needs.length == 0) return {ok: true, alloc: {}, spend: {}, shortfall: {}};

    let alloc;
    try{
        alloc = search(needs, wallet, rules, blockNative, prefer);
    }catch(e){
        if(!(e instanceof RangeError)) throw e;
        alloc = greedy(needs, wallet, rules, blockNative, prefer);
    }

    if(!alloc){
        // Report what could not be covered, for the caller's log.
        let shortfall = {};
        let partial = greedyShortfall(needs, wallet, rules, blockNative, prefer);
        for(let r of Object.keys(partial)) if(partial[r] > 0) shortfall[r] = partial[r];
        return {ok: false, alloc: {}, spend: {}, shortfall};
    }

    return {ok: true, alloc, spend: spendFrom(alloc), shortfall: {}};
}

function greedyShortfall(needs, wallet, rules, noSub, prefer){
    let left = {...wallet};
    let short = {};
    for(let need of needs){
        let remaining = need.amount;
        for(let p of payersFor(need.to, rules, noSub)){
            if(remaining <= 0) break;
            let can = Math.min(Math.ceil(remaining / p.get), Math.floor((left[p.from] || 0) / p.give));
            if(can <= 0) continue;
            left[p.from] -= can * p.give;
            remaining -= can * p.get;
        }
        if(remaining > 0) short[need.to] = remaining;
    }
    return short;
}

// ---------------------------------------------------------------------------------------------
// Adjusting an allocation: the +/- controls on the payment prompt.
//
// A move is {to, from, dir}: dir '+' applies one more unit of the (from -> to) rule, '-' takes
// one back. Both go through adjust(), which rebuilds the allocation and validates it against the
// wallet, so there is no bespoke arithmetic to keep in step with the solver.

function ruleFor(rules, to, from){
    let candidates = rules.filter(r => r.to == to && r.from == from);
    if(candidates.length == 0) return false;
    // Best rate first, so a card granting a better rate than the builtin wins.
    return candidates.sort((a, b) => a.give / a.get - b.give / b.get)[0];
}

/**
 * Apply one +/- step to an allocation. Returns a new {alloc, spend}, or false if the move is
 * not available (nothing left to take back, or not enough of `from` to give).
 */
export function adjust({need = {}, alloc = {}, wallet = {}, rules = []} = {}, {to, from, dir} = {}){
    let rule = ruleFor(rules, to, from);
    if(!rule) return false;
    if(!need[to]) return false;

    let next = {};
    for(let k of Object.keys(alloc)) next[k] = {...alloc[k]};
    if(!next[to]) next[to] = {};

    let units = next[to][from] || 0;
    if(dir == '+'){
        units += rule.give;
    }else{
        if(units < rule.give) return false;
        units -= rule.give;
    }
    if(units > 0) next[to][from] = units; else delete next[to][from];

    // Native cover picks up whatever the rules no longer cover.
    let covered = 0;
    for(let f of Object.keys(next[to])){
        if(f == to) continue;
        let r = ruleFor(rules, to, f);
        if(!r) continue;
        covered += (next[to][f] / r.give) * r.get;
    }
    let native = Math.max(0, need[to] - covered);
    if(native > 0) next[to][to] = native; else delete next[to][to];

    let spend = spendFrom(next);
    for(let r of Object.keys(spend)){
        if(spend[r] > (wallet[r] || 0)) return false; // cannot afford the adjusted allocation
    }
    // A '+' that buys nothing (the rules already cover the whole requirement) is not a move.
    if(dir == '+' && covered - rule.get >= need[to]) return false;
    return {alloc: next, spend};
}

export function canAdjust(state, move){
    return !!adjust(state, move);
}

/**
 * The (to, from) pairs the payment prompt should offer a control for: one per substitution that
 * is either available now or already in use.
 */
export function adjustable(state){
    let {need = {}, alloc = {}, rules = []} = state || {};
    let pairs = [];
    let seen = new Set();
    for(let to of Object.keys(need).sort()){
        if(!need[to]) continue;
        for(let rule of rules.filter(r => r.to == to)){
            let key = `${to}<${rule.from}`;
            if(seen.has(key)) continue;
            let inUse = !!(alloc[to] && alloc[to][rule.from]);
            if(!inUse && !canAdjust(state, {to, from: rule.from, dir: '+'})) continue;
            seen.add(key);
            pairs.push({to, from: rule.from});
        }
    }
    return pairs;
}

/**
 * Which requirements a given pay-resource was substituted into, for the reaction hooks:
 * spendingSubstitutions(alloc, 'glory') -> [{to: 'dilithium', units: 1}]
 */
export function spendingSubstitutions(alloc, from){
    let out = [];
    for(let to of Object.keys(alloc || {}).sort()){
        if(to == from) continue;
        let units = alloc[to][from];
        if(units) out.push({to, units});
    }
    return out;
}

/**
 * Order resources for rendering and payment: the familiar ones first, then anything else
 * alphabetically, so a new resource type appears without reordering the existing ones.
 */
export function spendOrder(resources){
    let known = SPEND_ORDER.filter(r => resources.includes(r));
    let rest = resources.filter(r => !SPEND_ORDER.includes(r)).sort();
    return [...known, ...rest];
}
